import memoize from "lodash.memoize";
import { perspectiveWarpShader, vertexShader } from "./shaders/source";
import { bindTexture, buildProgram, executeShader, flushProgram, initializePerspectiveTransformProgram } from "./shaders/utils";
import { getPerspectiveWarp, transpose3x31D } from "./shaders/matrix";
import { releaseCanvasses } from "./releaseCanvasses";
export function paint(_ref) {
  let {
    outputCtx,
    document,
    dimensions,
    scene,
    debug
  } = _ref;
  const {
    sceneCanvas,
    sceneCtx
  } = getSceneCanvas(debug === null || debug === void 0 ? void 0 : debug.canvasContainerId);
  const {
    docCompositeCanvas,
    docCompositeCtx
  } = getDocumentComposite(debug === null || debug === void 0 ? void 0 : debug.canvasContainerId);
  // Initialize and clear the scene canvas
  sceneCanvas.width = scene.width;
  sceneCanvas.height = scene.height;
  sceneCtx.globalCompositeOperation = "source-over";
  sceneCtx.clearRect(0, 0, scene.width, scene.height);
  // Initialize and clear the document composite canvas
  docCompositeCanvas.width = scene.width;
  docCompositeCanvas.height = scene.height;
  docCompositeCtx.globalCompositeOperation = "source-over";
  docCompositeCtx.clearRect(0, 0, scene.width, scene.height);
  // Draw a background if it exist
  if (scene.background) {
    sceneCtx.drawImage(scene.background, 0, 0);
  }
  // Prepare the mask before the document element is rendered
  docCompositeCtx.drawImage(scene.mask, 0, 0);
  docCompositeCtx.globalCompositeOperation = "source-in";
  // Render the first document only
  const documentElement = scene.documents[0];
  // Simple rectangle warp
  const {
    shaderCanvas,
    glCtx
  } = getShaderCanvas(debug === null || debug === void 0 ? void 0 : debug.canvasContainerId);
  const perspectiveWarpShader = loadPerspectiveWarpShader(glCtx);
  shaderCanvas.width = scene.width;
  shaderCanvas.height = scene.height;
  glCtx.useProgram(perspectiveWarpShader);
  const docTexture = bindTexture(glCtx, document, glCtx.LINEAR);
  const src = [];
  const dst = [];
  for (const mapping of documentElement.transform.pointMappings) {
    src.push([mapping.source.x, mapping.source.y]);
    dst.push([mapping.destination.x, mapping.destination.y]);
  }
  let perspective = getPerspectiveWarp(src, dst);
  perspective = transpose3x31D([...perspective, 1]);
  const {
    vertexBuffer,
    fragmentBuffer
  } = initializePerspectiveTransformProgram({
    ctx: glCtx,
    width: scene.width,
    height: scene.height,
    program: perspectiveWarpShader,
    document: docTexture,
    matrixValues: perspective
  });
  executeShader(glCtx);
  flushProgram(glCtx, vertexBuffer, fragmentBuffer);
  docCompositeCtx.drawImage(shaderCanvas, 0, 0);
  // Set the blending mode
  if (documentElement.blendingMode === "multiply") {
    sceneCtx.globalCompositeOperation = "multiply";
  }
  sceneCtx.drawImage(docCompositeCanvas, 0, 0);
  // Draw an overlay if it exist
  if (scene.overlay) {
    sceneCtx.globalCompositeOperation = "screen";
    sceneCtx.drawImage(scene.overlay, 0, 0);
  }
  // Setup the output canvas to the requested dimensions
  outputCtx.canvas.width = dimensions.width;
  outputCtx.canvas.height = dimensions.height;
  outputCtx.save();
  outputCtx.clearRect(0, 0, dimensions.width, dimensions.height);
  outputCtx.drawImage(sceneCanvas, 0, 0, scene.width, scene.height, 0, 0, dimensions.width, dimensions.height);
  releaseCanvasses([sceneCanvas, docCompositeCanvas, shaderCanvas]);
  outputCtx.restore();
}
// Offscreen canvas used for intermediate scene operations
const getSceneCanvas = memoize(debugContainerId => {
  const sceneCanvas = document.createElement("canvas");
  sceneCanvas.id = "flash-sceneCanvas-".concat(Math.random()); // Add random to make sure it's unique
  if (debugContainerId) {
    const debugContainerElement = document.getElementById(debugContainerId);
    if (!debugContainerElement) {
      console.warn("debugContainerElement width id ".concat(debugContainerId, " not found"));
    } else {
      debugContainerElement.append(sceneCanvas);
    }
  }
  const sceneCtx = sceneCanvas.getContext("2d");
  return {
    sceneCanvas,
    sceneCtx
  };
}, () => "1" // Always cache
);
// Offscreen canvas used for intermediate scene operations
const getDocumentComposite = memoize(debugContainerId => {
  const docCompositeCanvas = document.createElement("canvas");
  docCompositeCanvas.id = "flash-docCompositeCanvas-".concat(Math.random()); // Add random to make sure it's unique
  if (debugContainerId) {
    const debugContainerElement = document.getElementById(debugContainerId);
    if (!debugContainerElement) {
      console.warn("debugContainerElement width id ".concat(debugContainerId, " not found"));
    } else {
      debugContainerElement.append(docCompositeCanvas);
    }
  }
  const docCompositeCtx = docCompositeCanvas.getContext("2d");
  return {
    docCompositeCanvas,
    docCompositeCtx
  };
}, () => "1" // Always cache
);
// WebGL canvas for performing the shader
const getShaderCanvas = memoize(debugContainerId => {
  var _a;
  const shaderCanvas = document.createElement("canvas");
  shaderCanvas.id = "flash-shaderCanvas-".concat(Math.random()); // Add random to make sure it's unique
  if (debugContainerId) {
    const debugContainerElement = document.getElementById(debugContainerId);
    if (!debugContainerElement) {
      console.warn("debugContainerElement width id ".concat(debugContainerId, " not found"));
    } else {
      debugContainerElement.append(shaderCanvas);
    }
  }
  const glCtx = (_a = shaderCanvas.getContext("webgl", {
    premultipliedAlpha: true
  })) !== null && _a !== void 0 ? _a : shaderCanvas.getContext("experimental-webgl", {
    premultipliedAlpha: true
  });
  if (!glCtx) {
    throw new Error("WebGL not supported");
  }
  glCtx.pixelStorei(glCtx.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  return {
    shaderCanvas,
    glCtx
  };
}, () => "1" // Always cache
);
const loadPerspectiveWarpShader = memoize(glCtx => {
  // Bind webgl context as the base texture
  bindTexture(glCtx, undefined, glCtx.NEAREST);
  return buildProgram(glCtx, vertexShader, perspectiveWarpShader);
}, () => {
  return "1"; // always cache
});